/**
 * Admin Routes Configuration
 *
 * Admin 라우트 설정
 * - 경로와 컴포넌트를 분리하여 관리
 * - Lazy loading 적용으로 초기 번들 크기 감소
 */

import React, { lazy } from "react";

// Lazy load Admin pages
const OrganizationList = lazy(
  () => import("../../pages/manager/OrganizationList"),
);
const UserManagement = lazy(() => import("../../pages/users/UserManagement"));
const AdminScenarioManagement = lazy(
  () => import("../../pages/scenario/AdminScenarioManagement"),
);
const AdminScenarioAdd = lazy(
  () => import("../../pages/scenario/AdminScenarioAdd"),
);
const AdminScenarioEdit = lazy(
  () => import("../../pages/scenario/AdminScenarioEdit"),
);
const ScenarioDetail = lazy(() =>
  import("../../pages/scenario").then((m) => ({ default: m.ScenarioDetail })),
);
const ClassManagement = lazy(() => import("../../pages/class/ClassManagement"));
const ProductManagement = lazy(
  () => import("../../pages/product/ProductManagement"),
);
const ProductCreate = lazy(() => import("../../pages/product/ProductCreate"));
const ClassCreate = lazy(() => import("../../pages/class/ClassCreate"));
const ClassEdit = lazy(() => import("../../pages/class/ClassEdit"));
const ClassDetail = lazy(() => import("../../pages/class/ClassDetail"));
const RedirectToMyClassDetail = lazy(() =>
  import("../../pages/class/RedirectToMyClassDetail").then((m) => ({
    default: m.default,
  })),
);
const ProductEdit = lazy(() => import("../../pages/product/ProductEdit"));
const ProductDetail = lazy(() => import("../../pages/product/ProductDetail"));
const EventManagement = lazy(
  () => import("../../pages/assets/EventManagement"),
);
const EventCreate = lazy(() => import("../../pages/assets/EventCreate"));
const EventEdit = lazy(() => import("../../pages/assets/EventEdit"));
const DialogueManagement = lazy(
  () => import("../../pages/assets/DialogueManagement"),
);
const ItemManagement = lazy(() => import("../../pages/assets/ItemManagement"));
const SymptomManagement = lazy(
  () => import("../../pages/assets/SymptomManagement"),
);
const AssetTaskManagement = lazy(
  () => import("../../pages/assets/AssetTaskManagement"),
);
const AssetActionManagement = lazy(
  () => import("../../pages/assets/AssetActionManagement"),
);
const OrderManagement = lazy(() => import("../../pages/order/OrderManagement"));
const OrderDetail = lazy(() => import("../../pages/order/OrderDetail"));
const OrderCreate = lazy(() => import("../../pages/order/OrderCreate"));
const ResourceList = lazy(() => import("../../pages/resources/ResourceList"));
const ResourceCreate = lazy(
  () => import("../../pages/resources/ResourceCreate"),
);
const ResourceEdit = lazy(() => import("../../pages/resources/ResourceEdit"));
const ResourceDetail = lazy(
  () => import("../../pages/resources/ResourceDetail"),
);
const BlogList = lazy(() => import("../../pages/blog/BlogList"));
const BlogDetail = lazy(() => import("../../pages/blog/BlogDetail"));
const BlogCreate = lazy(() => import("../../pages/blog/BlogCreate"));
const BlogEdit = lazy(() => import("../../pages/blog/BlogEdit"));
const FaqManagement = lazy(() => import("../../pages/faq/FaqManagement"));
const FaqCreate = lazy(() => import("../../pages/faq/FaqCreate"));
const FaqDetail = lazy(() => import("../../pages/faq/FaqDetail"));
const FaqEdit = lazy(() => import("../../pages/faq/FaqEdit"));

export interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
}

/** 상대 경로 (부모 path="/admin" 기준). 중첩 라우트에서 useParams 등 동작 보장 */
export const adminRoutes: RouteConfig[] = [
  { path: "organizations", component: OrganizationList },
  { path: "users", component: UserManagement },
  { path: "resources", component: ResourceList },
  { path: "resources/create", component: ResourceCreate },
  { path: "resources/:id/edit", component: ResourceEdit },
  { path: "resources/:id", component: ResourceDetail },
  { path: "blog", component: BlogList },
  { path: "blog/create", component: BlogCreate },
  { path: "blog/:id/edit", component: BlogEdit },
  { path: "blog/:id", component: BlogDetail },
  { path: "faq", component: FaqManagement },
  { path: "faq/create", component: FaqCreate },
  { path: "faq/:id/edit", component: FaqEdit },
  { path: "faq/:id", component: FaqDetail },
  { path: "scenarios", component: AdminScenarioManagement },
  { path: "order-management", component: OrderManagement },
  { path: "order-management/create", component: OrderCreate },
  { path: "order-management/:orderId/edit", component: OrderCreate },
  { path: "order-management/:orderId", component: OrderDetail },
  { path: "order-create", component: OrderCreate },
  { path: "products/:id", component: ClassDetail },
  { path: "scenarios/add", component: AdminScenarioAdd },
  { path: "scenarios/:id/edit", component: AdminScenarioEdit },
  { path: "scenarios/:id", component: ScenarioDetail },
  { path: "class-management", component: ClassManagement },
  { path: "class-management/:id", component: RedirectToMyClassDetail },
  { path: "product-management", component: ProductManagement },
  { path: "product/create", component: ProductCreate },
  { path: "open-class/create", component: ClassCreate },
  { path: "open-class/edit/:id", component: ClassEdit },
  { path: "open-class/duplicate/:id", component: ClassEdit },
  { path: "open-class/:id", component: ClassDetail },
  { path: "my-classes/:id", component: ClassDetail },
  { path: "product/detail/:id", component: ProductDetail },
  { path: "product/edit/:id", component: ProductEdit },
  { path: "class/create", component: ClassCreate },
  { path: "class/duplicate/:id", component: ClassEdit },
  { path: "class/edit/:id", component: ClassEdit },
  { path: "assets/events", component: EventManagement },
  { path: "assets/events/create", component: EventCreate },
  { path: "assets/events/:id/edit", component: EventEdit },
  { path: "assets/symptoms", component: SymptomManagement },
  { path: "assets/tasks", component: AssetTaskManagement },
  { path: "assets/actions", component: AssetActionManagement },
  { path: "assets/dialogues", component: DialogueManagement },
  { path: "assets/items", component: ItemManagement },
];
